import DOMPurify from "dompurify";
import truncate from "lodash/truncate";
import { marked } from "marked";

export const noImageRenderer = {
    image() {
        return "";
    },
};

export const CHAT_SANITIZE_CONFIG = {
    ADD_ATTR: ["target", "ref"],
    FORBID_TAGS: ["img"],
};

export function sanitizeForChat(html, extraConfig = {}) {
    return DOMPurify.sanitize(html, {
        ...CHAT_SANITIZE_CONFIG,
        ...extraConfig,
    });
}

function truncateWords(text, length) {
    return truncate(text, { length, omission: "", separator: /\s+/ });
}

// Keeps the leading part of an oversized block that still parses as the same Markdown: whole inline
// tokens of a paragraph (word-cut only inside plain text), or whole rows of a table or items of a list.
function truncateBlock(token, budget) {
    let kept = "";
    if (token.type === "paragraph") {
        for (const inline of token.tokens) {
            if (kept.length + inline.raw.length <= budget) {
                kept += inline.raw;
                continue;
            }
            if (inline.type === "text") {
                kept += truncateWords(inline.raw, budget - kept.length);
            }
            break;
        }
        return { text: kept, inline: true };
    }
    if (token.type === "table") {
        const [header, delimiter, ...rows] = token.raw.trimEnd().split("\n");
        kept = `${header}\n${delimiter}\n`;
        if (kept.length > budget) {
            return { text: "", inline: false };
        }
        for (const row of rows) {
            if (kept.length + row.length + 1 > budget) {
                break;
            }
            kept += `${row}\n`;
        }
        return { text: kept, inline: false };
    }
    if (token.type === "list") {
        for (const item of token.items) {
            if (kept.length + item.raw.length > budget) {
                break;
            }
            kept += item.raw;
        }
        return { text: kept, inline: false };
    }
    return { text: "", inline: false };
}

// Shortens Markdown to roughly `limit` characters without ending inside a token (a dangling `**`,
// half a table row). Whole blocks are kept while they fit; the block that overflows is cut by
// `truncateBlock`.
export function truncateMarkdown(markdown, limit, omission = "...") {
    if (markdown.length <= limit) {
        return markdown;
    }
    let kept = "";
    let endsInline = false;
    for (const token of marked.lexer(markdown)) {
        if (kept.length + token.raw.length <= limit) {
            kept += token.raw;
            continue;
        }
        const partial = truncateBlock(token, limit - kept.length);
        if (partial.text.trim()) {
            kept += partial.text;
            endsInline = partial.inline;
        }
        break;
    }
    if (!kept.trim()) {
        return `${truncateWords(markdown, limit).trimEnd()} ${omission}`;
    }
    return endsInline ? `${kept.trimEnd()} ${omission}` : `${kept.trimEnd()}\n\n${omission}`;
}
